/**
 * Visual rule builder for the policy DSL.
 *
 * The DSL is a tree of leaf comparators (``{field, op, value}``) and
 * boolean groups (``{op: and|or|not, conditions: [...]}``). The UI here
 * targets the **common case** spelled out by the spec's seed: each rule
 * is a flat AND of comparators. Rules whose conditions don't match that
 * shape stay editable as raw JSON so power users keep full DSL access
 * without us having to render arbitrary nesting.
 *
 * On save, the editor POSTs the entire rule set as a NEW policy version
 * — versioning is preserved by design (ADR-007).
 */
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createPolicyVersion,
  type PolicyVersion,
  type RuleInPayload,
} from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Label, NumberInput, Select, TextInput } from "@/components/ui/Input";
import { Pill } from "@/components/ui/Pill";
import { Panel } from "@/components/ui/Panel";

// ---------------------------------------------------------------------------
// DSL shapes (mirrors ``services/policy/dsl.py``)
// ---------------------------------------------------------------------------

const NUMERIC_FIELDS = [
  "composite",
  "valuation_score",
  "quality_score",
  "growth_score",
  "momentum_score",
  "revisions_score",
  "risk_score",
  "coverage",
] as const;

const ENUM_FIELDS = ["confidence_band", "thesis_status"] as const;

const ENUM_VALUES: Record<string, string[]> = {
  confidence_band: ["high", "medium", "low", "insufficient"],
  thesis_status: ["improving", "stable", "deteriorating", "unknown"],
};

const NUMERIC_OPS = [
  "eq",
  "neq",
  "gt",
  "gte",
  "lt",
  "lte",
  "between",
  "is_null",
  "is_not_null",
] as const;
const ENUM_OPS = [
  "eq",
  "neq",
  "in",
  "not_in",
  "is_null",
  "is_not_null",
] as const;

const STANCES = [
  "strong_candidate_for_review",
  "candidate_for_accumulation_review",
  "hold_and_monitor",
  "reduce_and_review",
  "exit_review_candidate",
  "no_stance",
  "insufficient_quality",
] as const;

const STANCE_LABELS: Record<string, string> = {
  strong_candidate_for_review: "Strong candidate for review",
  candidate_for_accumulation_review: "Candidate for accumulation review",
  hold_and_monitor: "Hold and monitor",
  reduce_and_review: "Reduce and review",
  exit_review_candidate: "Exit review candidate",
  no_stance: "No stance",
  insufficient_quality: "Insufficient quality",
};

type Field = string;
type Op = string;

type Comparator = {
  field: Field;
  op: Op;
  value: unknown;
};

type ConditionGroup = {
  op: "and" | "or" | "not";
  conditions: Array<Comparator | ConditionGroup>;
};

// Editable rule held in local state.
type EditableRule = {
  // Stable id for React keying (rule.id from the server, or a synthetic
  // negative number for new local rules).
  key: number;
  priority: number;
  name: string;
  output_stance: string;
  explanation_template: string;
  // Either a flat AND/OR/NOT-of-comparators (visual-mode) or a raw JSON
  // string (raw-mode) for arbitrary nesting.
  mode: "flat" | "raw";
  flatOp: "and" | "or" | "not";  // top-level combinator for visual mode
  flatComparators: Comparator[];  // for mode === "flat"
  rawJson: string;                // for mode === "raw"
};

// ---------------------------------------------------------------------------
// Detect whether a rule's conditions tree is a flat AND/OR/NOT of comparators.
// ---------------------------------------------------------------------------

function isComparator(c: unknown): c is Comparator {
  return (
    typeof c === "object" &&
    c !== null &&
    "field" in c &&
    "op" in c &&
    !("conditions" in c)
  );
}

/** Returns true for any single-level group whose children are all leaf comparators. */
function isFlatGroup(conditions: unknown): conditions is ConditionGroup {
  if (typeof conditions !== "object" || conditions === null) return false;
  const c = conditions as ConditionGroup;
  if (c.op !== "and" && c.op !== "or" && c.op !== "not") return false;
  if (!Array.isArray(c.conditions)) return false;
  return c.conditions.every((cc) => isComparator(cc));
}

// ---------------------------------------------------------------------------
// Convert a rule from the API into editor state.
// ---------------------------------------------------------------------------

function toEditable(rule: PolicyVersion["rules"][number]): EditableRule {
  const conditions = rule.conditions as unknown;
  if (isFlatGroup(conditions)) {
    const cg = conditions as ConditionGroup;
    return {
      key: rule.id,
      priority: rule.priority,
      name: rule.name,
      output_stance: rule.output_stance,
      explanation_template: rule.explanation_template ?? "",
      mode: "flat",
      flatOp: cg.op,
      flatComparators: cg.conditions.map((c) => ({ ...(c as Comparator) })),
      rawJson: JSON.stringify(conditions, null, 2),
    };
  }
  return {
    key: rule.id,
    priority: rule.priority,
    name: rule.name,
    output_stance: rule.output_stance,
    explanation_template: rule.explanation_template ?? "",
    mode: "raw",
    flatOp: "and",
    flatComparators: [],
    rawJson: JSON.stringify(conditions, null, 2),
  };
}

function blankRule(nextKey: number): EditableRule {
  return {
    key: nextKey,
    priority: 100,
    name: "new_rule",
    output_stance: "hold_and_monitor",
    explanation_template: "",
    mode: "flat",
    flatOp: "and",
    flatComparators: [{ field: "composite", op: "gte", value: 70 }],
    rawJson: '{"op":"and","conditions":[]}',
  };
}

function defaultOpForField(field: string): string {
  return ENUM_FIELDS.includes(field as never) ? "eq" : "gte";
}

function defaultValueForOp(field: string, op: string): unknown {
  if (op === "is_null" || op === "is_not_null") return null;
  if (op === "between") return [0, 100];
  if (op === "in" || op === "not_in") return [];
  if (ENUM_FIELDS.includes(field as never)) {
    return ENUM_VALUES[field]?.[0] ?? "";
  }
  return 0;
}

// ---------------------------------------------------------------------------
// Client-side expression renderer (mirrors services/policy/dsl.py render()).
// Used for live previews — never sent to the server.
// ---------------------------------------------------------------------------

const OP_DISPLAY: Record<string, string> = {
  eq: "=",
  neq: "≠",
  gt: ">",
  gte: "≥",
  lt: "<",
  lte: "≤",
  between: "BETWEEN",
  in: "IN",
  not_in: "NOT IN",
  is_null: "IS NULL",
  is_not_null: "IS NOT NULL",
};

function renderComparator(c: Comparator): string {
  const opStr = OP_DISPLAY[c.op] ?? c.op.toUpperCase();
  if (c.op === "is_null" || c.op === "is_not_null") {
    return `${c.field} ${opStr}`;
  }
  if (c.op === "between" && Array.isArray(c.value)) {
    const [lo, hi] = c.value as [number, number];
    return `${c.field} BETWEEN ${lo} AND ${hi}`;
  }
  if ((c.op === "in" || c.op === "not_in") && Array.isArray(c.value)) {
    const vals = (c.value as unknown[]).join(", ");
    return `${c.field} ${opStr} [${vals}]`;
  }
  return `${c.field} ${opStr} ${String(c.value)}`;
}

function renderFlatConditions(flatOp: "and" | "or" | "not", comparators: Comparator[]): string {
  if (comparators.length === 0) return "(no conditions)";
  if (flatOp === "not") {
    return comparators[0] ? `NOT (${renderComparator(comparators[0])})` : "NOT ()";
  }
  if (comparators.length === 1) {
    return renderComparator(comparators[0]!);
  }
  return `(${comparators.map(renderComparator).join(` ${flatOp.toUpperCase()} `)})`;
}

function renderRawExpression(rawJson: string): string {
  try {
    const parsed = JSON.parse(rawJson) as ConditionGroup | Comparator;
    return renderNode(parsed);
  } catch {
    return "(invalid JSON)";
  }
}

function renderNode(node: ConditionGroup | Comparator): string {
  if (isComparator(node)) return renderComparator(node);
  const { op, conditions } = node;
  if (op === "not") {
    const child = conditions[0];
    return child ? `NOT (${renderNode(child as ConditionGroup | Comparator)})` : "NOT ()";
  }
  const parts = conditions.map((c) => renderNode(c as ConditionGroup | Comparator));
  return parts.length === 1 ? parts[0]! : `(${parts.join(` ${op.toUpperCase()} `)})`;
}

// ---------------------------------------------------------------------------
// Build the API payload from editable rules.
// ---------------------------------------------------------------------------

function buildPayload(rules: EditableRule[]): {
  ok: true;
  payload: RuleInPayload[];
} | { ok: false; error: string; ruleKey: number } {
  const out: RuleInPayload[] = [];
  for (const r of rules) {
    let conditions: Record<string, unknown>;
    if (r.mode === "flat") {
      for (const c of r.flatComparators) {
        if (c.op === "between" && Array.isArray(c.value)) {
          const lo = (c.value as number[])[0] ?? 0;
          const hi = (c.value as number[])[1] ?? 0;
          if (lo > hi)
            return {
              ok: false,
              error: `Rule '${r.name}': BETWEEN low (${lo}) must be ≤ high (${hi})`,
              ruleKey: r.key,
            };
        }
        if (
          (c.op === "in" || c.op === "not_in") &&
          Array.isArray(c.value) &&
          c.value.length === 0
        ) {
          return {
            ok: false,
            error: `Rule '${r.name}': '${c.op}' requires at least one value`,
            ruleKey: r.key,
          };
        }
      }
      conditions = {
        op: r.flatOp,
        conditions: r.flatComparators.map((c) => ({ ...c })),
      };
    } else {
      try {
        conditions = JSON.parse(r.rawJson);
      } catch (err) {
        return {
          ok: false,
          error: `Invalid JSON in rule '${r.name}': ${(err as Error).message}`,
          ruleKey: r.key,
        };
      }
    }
    out.push({
      priority: r.priority,
      name: r.name,
      conditions,
      output_stance: r.output_stance,
      explanation_template: r.explanation_template.trim() || null,
    });
  }
  return { ok: true, payload: out };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function RuleBuilderPanel({
  source,
}: {
  source: PolicyVersion | null;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState<string>(source?.name ?? "default");
  const [description, setDescription] = useState<string>("");
  const [rules, setRules] = useState<EditableRule[]>([]);
  const [activate, setActivate] = useState<boolean>(true);
  const [buildError, setBuildError] = useState<string | null>(null);
  const nextKey = useMemo(
    () =>
      rules.length
        ? Math.min(0, ...rules.map((r) => r.key)) - 1
        : -1,
    [rules],
  );

  // (Re-)hydrate when the source version changes — e.g. after a save bumps
  // to a new version, or the operator switches versions in the picker.
  useEffect(() => {
    if (source) {
      setName(source.name);
      setRules(source.rules.map(toEditable));
    }
  }, [source]);

  const create = useMutation({
    mutationFn: (payload: {
      name: string;
      description: string | null;
      rules: RuleInPayload[];
      activate: boolean;
    }) => createPolicyVersion(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["policy"] }),
  });

  function addRule() {
    setRules((prev) => [...prev, blankRule(nextKey)]);
  }

  function removeRule(key: number) {
    setRules((prev) => prev.filter((r) => r.key !== key));
  }

  function patchRule(key: number, patch: Partial<EditableRule>) {
    setRules((prev) =>
      prev.map((r) => (r.key === key ? { ...r, ...patch } : r)),
    );
  }

  function patchComparator(
    key: number,
    idx: number,
    patch: Partial<Comparator>,
  ) {
    setRules((prev) =>
      prev.map((r) => {
        if (r.key !== key) return r;
        const next = [...r.flatComparators];
        const merged: Comparator = { ...next[idx], ...patch } as Comparator;
        next[idx] = merged;
        return { ...r, flatComparators: next };
      }),
    );
  }

  function addComparator(key: number) {
    setRules((prev) =>
      prev.map((r) =>
        r.key === key
          ? {
              ...r,
              flatComparators: [
                ...r.flatComparators,
                { field: "composite", op: "gte", value: 70 },
              ],
            }
          : r,
      ),
    );
  }

  function removeComparator(key: number, idx: number) {
    setRules((prev) =>
      prev.map((r) =>
        r.key === key
          ? {
              ...r,
              flatComparators: r.flatComparators.filter((_, i) => i !== idx),
            }
          : r,
      ),
    );
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const built = buildPayload(rules);
    if (!built.ok) {
      setBuildError(built.error);
      return;
    }
    setBuildError(null);
    create.mutate({
      name,
      description: description.trim() || null,
      rules: built.payload,
      activate,
    });
  }

  return (
    <Panel
      title="Rule editor"
      description={
        source
          ? `Editing a copy of ${source.name} v${source.version}. Saving creates a new version.`
          : "Pick a version above to start editing."
      }
    >
      <form className="space-y-4" onSubmit={handleSubmit}>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="rb-name">Policy name</Label>
            <TextInput
              id="rb-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="default"
              required
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="rb-description">Description (this version)</Label>
            <TextInput
              id="rb-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional"
            />
          </div>
        </div>

        <div className="space-y-3">
          {rules.length === 0 && (
            <div className="rounded-md border border-dashed border-border bg-surface-base px-3 py-6 text-center text-xs text-foreground-muted">
              No rules yet. Add one to get started.
            </div>
          )}
          {[...rules]
            .sort((a, b) => a.priority - b.priority)
            .map((rule) => (
              <RuleCard
                key={rule.key}
                rule={rule}
                onPatch={(patch) => patchRule(rule.key, patch)}
                onPatchComparator={(idx, patch) =>
                  patchComparator(rule.key, idx, patch)
                }
                onAddComparator={() => addComparator(rule.key)}
                onRemoveComparator={(idx) => removeComparator(rule.key, idx)}
                onRemove={() => removeRule(rule.key)}
              />
            ))}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-3">
          <Button type="button" variant="secondary" size="sm" onClick={addRule}>
            + Add rule
          </Button>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={activate}
              onChange={(e) => setActivate(e.target.checked)}
            />
            Activate the new version
          </label>
          <Button
            type="submit"
            variant="primary"
            disabled={!name.trim() || create.isPending}
            loading={create.isPending}
          >
            Save as new version
          </Button>
        </div>

        {(create.isError || buildError) && (
          <div className="text-xs text-danger">
            {buildError ?? (create.error as Error).message}
          </div>
        )}
        {create.isSuccess && (
          <div className="text-xs text-foreground-muted">
            Saved {create.data.name} v{create.data.version}
            {create.data.is_active ? " (active)" : ""}.
          </div>
        )}
      </form>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Rule card
// ---------------------------------------------------------------------------

function RuleCard({
  rule,
  onPatch,
  onPatchComparator,
  onAddComparator,
  onRemoveComparator,
  onRemove,
}: {
  rule: EditableRule;
  onPatch: (p: Partial<EditableRule>) => void;
  onPatchComparator: (idx: number, p: Partial<Comparator>) => void;
  onAddComparator: () => void;
  onRemoveComparator: (idx: number) => void;
  onRemove: () => void;
}) {
  return (
    <div className="space-y-3 rounded-md border border-border bg-surface-base p-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1 w-20">
          <Label htmlFor={`rb-pri-${rule.key}`}>Priority</Label>
          <NumberInput
            id={`rb-pri-${rule.key}`}
            value={rule.priority}
            onChange={(e) => onPatch({ priority: Number(e.target.value) || 0 })}
            min={0}
            className="text-right"
          />
        </div>
        <div className="space-y-1 flex-1 min-w-[12rem]">
          <Label htmlFor={`rb-name-${rule.key}`}>Rule name</Label>
          <TextInput
            id={`rb-name-${rule.key}`}
            value={rule.name}
            onChange={(e) => onPatch({ name: e.target.value })}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor={`rb-stance-${rule.key}`}>Output stance</Label>
          <Select
            id={`rb-stance-${rule.key}`}
            value={rule.output_stance}
            onChange={(e) => onPatch({ output_stance: e.target.value })}
          >
            {STANCES.map((s) => (
              <option key={s} value={s}>
                {STANCE_LABELS[s] ?? s}
              </option>
            ))}
          </Select>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onRemove}
        >
          Remove
        </Button>
      </div>

      <div className="space-y-1">
        <Label htmlFor={`rb-tpl-${rule.key}`}>Explanation template</Label>
        <TextInput
          id={`rb-tpl-${rule.key}`}
          value={rule.explanation_template}
          onChange={(e) => onPatch({ explanation_template: e.target.value })}
          placeholder="Optional human-readable template"
        />
      </div>

      <div className="rounded bg-surface-subtle px-2.5 py-1.5">
        <code className="text-xs font-mono text-foreground-secondary">
          {rule.mode === "flat"
            ? renderFlatConditions(rule.flatOp, rule.flatComparators)
            : renderRawExpression(rule.rawJson)}
        </code>
      </div>

      {rule.mode === "flat" ? (
        <FlatConditionsEditor
          comparators={rule.flatComparators}
          op={rule.flatOp}
          onOpChange={(op) => {
            // Switching to NOT: only one comparator is allowed.
            if (op === "not" && rule.flatComparators.length > 1) {
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              onPatch({ flatOp: op, flatComparators: [rule.flatComparators[0]!] });
            } else {
              onPatch({ flatOp: op });
            }
          }}
          onPatch={(idx, p) => onPatchComparator(idx, p)}
          onAdd={onAddComparator}
          onRemove={onRemoveComparator}
          onSwitchToRaw={() =>
            onPatch({
              mode: "raw",
              rawJson: JSON.stringify(
                { op: rule.flatOp, conditions: rule.flatComparators },
                null,
                2,
              ),
            })
          }
        />
      ) : (
        <RawConditionsEditor
          rawJson={rule.rawJson}
          onChange={(v) => onPatch({ rawJson: v })}
          onSwitchToVisual={(parsed) =>
            onPatch({
              mode: "flat",
              flatOp: parsed.op,
              flatComparators: parsed.conditions.map((c) => ({ ...(c as Comparator) })),
              rawJson: JSON.stringify(parsed, null, 2),
            })
          }
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Visual conditions editor (flat AND-of-comparators)
// ---------------------------------------------------------------------------

function FlatConditionsEditor({
  comparators,
  op,
  onOpChange,
  onPatch,
  onAdd,
  onRemove,
  onSwitchToRaw,
}: {
  comparators: Comparator[];
  op: "and" | "or" | "not";
  onOpChange: (op: "and" | "or" | "not") => void;
  onPatch: (idx: number, p: Partial<Comparator>) => void;
  onAdd: () => void;
  onRemove: (idx: number) => void;
  onSwitchToRaw: () => void;
}) {
  const pillLabel = op.toUpperCase();

  return (
    <div className="space-y-2 rounded-md border border-border-subtle bg-surface-subtle p-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-2xs uppercase tracking-wider text-foreground-muted">
            Conditions
          </span>
          <Select
            value={op}
            onChange={(e) => onOpChange(e.target.value as "and" | "or" | "not")}
            className="h-6 py-0 text-2xs"
            aria-label="Top-level combinator"
          >
            <option value="and">AND — all must match</option>
            <option value="or">OR — any must match</option>
            <option value="not">NOT — none must match</option>
          </Select>
        </div>
        <button
          type="button"
          className="text-2xs text-foreground-muted underline-offset-2 hover:text-foreground-primary hover:underline"
          onClick={onSwitchToRaw}
        >
          edit as JSON
        </button>
      </div>

      {op === "not" && (
        <p className="text-2xs text-warning">
          NOT takes exactly one condition. Adding more will be ignored by the
          engine; save as JSON for nested NOT logic.
        </p>
      )}

      <ul className="space-y-1.5">
        {comparators.map((c, idx) => (
          <li
            key={idx}
            className="flex flex-wrap items-center gap-2 rounded-md bg-surface-base px-2 py-1.5"
          >
            <Pill tone="muted">{pillLabel}</Pill>
            <FieldSelect
              value={c.field}
              onChange={(field) => {
                const nextOp = defaultOpForField(field);
                onPatch(idx, {
                  field,
                  op: nextOp,
                  value: defaultValueForOp(field, nextOp),
                });
              }}
            />
            <OpSelect
              field={c.field}
              value={c.op}
              onChange={(nextOp) =>
                onPatch(idx, { op: nextOp, value: defaultValueForOp(c.field, nextOp) })
              }
            />
            <ValueInput
              field={c.field}
              op={c.op}
              value={c.value}
              onChange={(v) => onPatch(idx, { value: v })}
            />
            <button
              type="button"
              className="ml-auto text-2xs text-foreground-muted hover:text-danger"
              onClick={() => onRemove(idx)}
              aria-label="Remove condition"
            >
              ×
            </button>
          </li>
        ))}
      </ul>
      {op !== "not" && (
        <Button type="button" variant="ghost" size="sm" onClick={onAdd}>
          + Add condition
        </Button>
      )}
    </div>
  );
}

function FieldSelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <Select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-44"
    >
      <optgroup label="numeric">
        {NUMERIC_FIELDS.map((f) => (
          <option key={f} value={f}>
            {f}
          </option>
        ))}
      </optgroup>
      <optgroup label="enum">
        {ENUM_FIELDS.map((f) => (
          <option key={f} value={f}>
            {f}
          </option>
        ))}
      </optgroup>
    </Select>
  );
}

function OpSelect({
  field,
  value,
  onChange,
}: {
  field: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const ops = ENUM_FIELDS.includes(field as never) ? ENUM_OPS : NUMERIC_OPS;
  return (
    <Select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-28"
    >
      {ops.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </Select>
  );
}

function ValueInput({
  field,
  op,
  value,
  onChange,
}: {
  field: string;
  op: string;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  if (op === "is_null" || op === "is_not_null") {
    return <span className="text-2xs text-foreground-muted">(no value)</span>;
  }
  if (op === "between") {
    const [lo, hi] = Array.isArray(value) ? value : [0, 0];
    return (
      <div className="flex items-center gap-1">
        <NumberInput
          value={lo as number}
          onChange={(e) =>
            onChange([Number(e.target.value), Number(hi)])
          }
          className="w-20 text-right"
        />
        <span className="text-2xs text-foreground-muted">…</span>
        <NumberInput
          value={hi as number}
          onChange={(e) =>
            onChange([Number(lo), Number(e.target.value)])
          }
          className="w-20 text-right"
        />
      </div>
    );
  }
  if (op === "in" || op === "not_in") {
    const list = Array.isArray(value) ? value : [];
    return (
      <TextInput
        value={list.join(", ")}
        onChange={(e) => {
          const parts = e.target.value
            .split(",")
            .map((s) => s.trim())
            .filter((s) => s.length > 0);
          onChange(
            ENUM_FIELDS.includes(field as never)
              ? parts
              : parts.map((s) => Number(s)).filter((n) => Number.isFinite(n)),
          );
        }}
        className="w-44"
        placeholder="comma,separated"
      />
    );
  }
  if (ENUM_FIELDS.includes(field as never)) {
    return (
      <Select
        value={String(value ?? "")}
        onChange={(e) => onChange(e.target.value)}
        className="w-32"
      >
        {(ENUM_VALUES[field] ?? []).map((v) => (
          <option key={v} value={v}>
            {v}
          </option>
        ))}
      </Select>
    );
  }
  return (
    <NumberInput
      value={(value as number) ?? 0}
      onChange={(e) => onChange(Number(e.target.value))}
      className="w-20 text-right"
    />
  );
}

// ---------------------------------------------------------------------------
// Raw JSON fallback editor
// ---------------------------------------------------------------------------

function RawConditionsEditor({
  rawJson,
  onChange,
  onSwitchToVisual,
}: {
  rawJson: string;
  onChange: (v: string) => void;
  onSwitchToVisual: (parsed: ConditionGroup) => void;
}) {
  const [parseError, setParseError] = useState<string | null>(null);

  function trySwitch() {
    try {
      const parsed = JSON.parse(rawJson);
      if (!isFlatGroup(parsed)) {
        setParseError(
          "Only flat AND/OR/NOT groups with leaf comparators can be shown visually. Use JSON for nested trees.",
        );
        return;
      }
      setParseError(null);
      onSwitchToVisual(parsed as ConditionGroup);
    } catch (err) {
      setParseError(`JSON parse error: ${(err as Error).message}`);
    }
  }

  return (
    <div className="space-y-1 rounded-md border border-border-subtle bg-surface-subtle p-2">
      <div className="flex items-center justify-between">
        <span className="text-2xs uppercase tracking-wider text-foreground-muted">
          Conditions (raw JSON — full DSL)
        </span>
        <button
          type="button"
          className="text-2xs text-foreground-muted underline-offset-2 hover:text-foreground-primary hover:underline"
          onClick={trySwitch}
        >
          switch to visual
        </button>
      </div>
      <textarea
        className="w-full rounded-md border border-border bg-surface-base px-2.5 py-1.5 font-mono text-xs text-foreground-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        rows={6}
        value={rawJson}
        onChange={(e) => {
          onChange(e.target.value);
          setParseError(null);
        }}
        spellCheck={false}
      />
      {parseError && (
        <p className="text-2xs text-warning">{parseError}</p>
      )}
    </div>
  );
}
